import { useEffect, useState } from "react";
import type { Staff, StaffEmploymentType, StaffRole, Store } from "@ai-pos/shared";
import { authApi, staffApi } from "../api/resources.js";
import { useAuthStore } from "../stores/authStore.js";

const EMPLOYMENT_TYPE_LABELS: Record<StaffEmploymentType, string> = {
  FULL_TIME: "社員",
  PART_TIME: "アルバイト",
};

const ROLE_LABELS: Record<StaffRole, string> = {
  OWNER: "オーナー",
  ADMIN: "管理者",
  STAFF: "一般スタッフ",
};

interface FormState {
  id: string | null;
  name: string;
  role: StaffRole;
  employmentType: StaffEmploymentType;
  isActive: boolean;
  pin: string;
  storeIds: string[];
  // 新規登録時にのみ使う（所属店舗を1つだけ明示的に選ばせるため）。編集時は無視する。
  storeId: string;
}

export function StaffAdmin() {
  const currentStaff = useAuthStore((state) => state.staff);
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const isOwner = currentStaff?.role === "OWNER";
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingStoreId, setTogglingStoreId] = useState<string | null>(null);

  const load = () => {
    staffApi
      .list()
      .then((res) => setStaffList(res.staff))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
    // GET /api/stores はオーナー専用のため、ここでは「自分がアクセス権を持つ店舗」だけを
    // 返す/api/auth/my-storesを使う（店舗アクセス権の付与自体、実行者が持つ店舗にしか
    // できない仕様のため、選択肢としてもこれで過不足ない）。
    authApi
      .myStores()
      .then((res) => setStores(res.stores))
      .catch(() => undefined);
  };

  useEffect(load, []);

  const storeName = (storeId: string) => stores.find((s) => s.id === storeId)?.name ?? storeId;

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        await staffApi.update(form.id, {
          name: form.name,
          role: form.role,
          employmentType: form.employmentType,
          isActive: form.isActive,
          ...(form.pin ? { pin: form.pin } : {}),
        });
      } else {
        if (!form.pin) throw new Error("PINを入力してください");
        if (!form.storeId) throw new Error("所属店舗を選択してください");
        await staffApi.create({
          name: form.name,
          role: form.role,
          employmentType: form.employmentType,
          pin: form.pin,
          storeId: form.storeId,
        });
      }
      setForm(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStoreAccess = async (staffId: string, storeId: string, hasAccess: boolean) => {
    setTogglingStoreId(storeId);
    setError(null);
    try {
      const res = hasAccess ? await staffApi.removeStoreAccess(staffId, storeId) : await staffApi.addStoreAccess(staffId, storeId);
      setForm((prev) => (prev && prev.id === staffId ? { ...prev, storeIds: res.staff.storeIds ?? [] } : prev));
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "店舗アクセス権の変更に失敗しました");
    } finally {
      setTogglingStoreId(null);
    }
  };

  return (
    <div className="stack">
      <div className="row">
        <h2 style={{ flex: 1 }}>スタッフ管理</h2>
        {/* スタッフの新規登録はオーナー専用（サーバー側でも検証済み）。非オーナーには
            そもそもボタン自体を出さない。*/}
        {isOwner && (
          <button
            className="btn btn-primary"
            onClick={() =>
              setForm({
                id: null,
                name: "",
                role: "STAFF",
                employmentType: "PART_TIME",
                isActive: true,
                pin: "",
                storeIds: [],
                storeId: activeStoreId ?? stores[0]?.id ?? "",
              })
            }
          >
            新規スタッフ
          </button>
        )}
      </div>
      <p style={{ color: "var(--color-text-muted)" }}>
        新規スタッフは登録時に選択した1店舗にのみアクセス権を持ちます。複数店舗を掛け持ちさせたい場合は編集画面から店舗を追加してください。
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>氏名</th>
              <th>権限</th>
              <th>雇用区分</th>
              <th>アクセス可能店舗</th>
              <th>状態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {staffList.map((staff) => (
              <tr key={staff.id}>
                <td>{staff.name}</td>
                <td>{ROLE_LABELS[staff.role]}</td>
                <td>{EMPLOYMENT_TYPE_LABELS[staff.employmentType]}</td>
                <td>{(staff.storeIds ?? []).map(storeName).join("、") || "-"}</td>
                <td>
                  {staff.isActive ? <span className="badge">有効</span> : <span className="badge badge-danger">無効</span>}
                  {staff.mustChangePin && <span className="badge badge-danger" style={{ marginLeft: 6 }}>PIN要変更</span>}
                </td>
                <td>
                  <button
                    className="btn"
                    onClick={() =>
                      setForm({
                        id: staff.id,
                        name: staff.name,
                        role: staff.role,
                        employmentType: staff.employmentType,
                        isActive: staff.isActive,
                        pin: "",
                        storeIds: staff.storeIds ?? [],
                        storeId: "",
                      })
                    }
                  >
                    編集
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal stack" onClick={(e) => e.stopPropagation()}>
            <h3>{form.id ? "スタッフ編集" : "新規スタッフ"}</h3>
            <div className="form-field">
              <label htmlFor="s-name">氏名</label>
              <input id="s-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            {!form.id && (
              <div className="form-field">
                <label htmlFor="s-store">所属店舗</label>
                <select id="s-store" value={form.storeId} onChange={(e) => setForm({ ...form, storeId: e.target.value })}>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-field">
              <label htmlFor="s-role">権限</label>
              <select id="s-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as StaffRole })}>
                <option value="STAFF">一般スタッフ</option>
                <option value="ADMIN">管理者</option>
                {/* オーナー権限の付与・剥奪はオーナー自身にしか許可しない（サーバー側でも検証済み）。
                    非オーナーには選択肢自体を出さない。*/}
                {(isOwner || form.role === "OWNER") && <option value="OWNER">オーナー</option>}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="s-employment-type">雇用区分</label>
              <select
                id="s-employment-type"
                value={form.employmentType}
                onChange={(e) => setForm({ ...form, employmentType: e.target.value as StaffEmploymentType })}
              >
                <option value="PART_TIME">アルバイト</option>
                <option value="FULL_TIME">社員</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="s-pin">
                {form.id ? "PIN再設定（変更する場合のみ入力）" : "PIN"}
                {form.role === "ADMIN" || form.role === "OWNER"
                  ? "（6桁・管理者/オーナーは総当たり対策のため6桁固定）"
                  : "（4〜6桁）"}
              </label>
              <input id="s-pin" type="text" inputMode="numeric" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
            </div>
            {form.id && (
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  style={{ minHeight: "auto", width: 20, height: 20 }}
                />
                有効
              </label>
            )}

            {form.id && stores.length > 1 && (
              <div className="form-field">
                <label>アクセス可能な店舗（掛け持ち設定）</label>
                {stores.map((store) => {
                  const hasAccess = form.storeIds.includes(store.id);
                  return (
                    <label key={store.id} className="row" style={{ gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={hasAccess}
                        disabled={togglingStoreId === store.id}
                        onChange={() => void handleToggleStoreAccess(form.id!, store.id, hasAccess)}
                        style={{ minHeight: "auto", width: 20, height: 20 }}
                      />
                      {store.name}
                    </label>
                  );
                })}
              </div>
            )}

            <div className="row">
              <button className="btn" onClick={() => setForm(null)}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !form.name}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
